// Copyright (c) 2026. Hardfin, Inc. All rights reserved.

/**
 * Anchored popover with a heading + body slot. Sits between MUI's
 * Tooltip (text-only, hover) and Dialog (centered, modal). The popover
 * closes only on outside click or Esc — passing the open/close state
 * through stays the caller's responsibility, and mutation success or
 * cache invalidation must NOT auto-close it.
 *
 * The render-at-root rule still applies — mount the shell once at the
 * parent component scope, drive its visibility with `open`.
 */
import {
  Box,
  Paper,
  Popover,
  PopoverActions,
  PopoverOrigin,
  Stack,
  Typography,
} from '@mui/material';
import { ReactNode, Ref } from 'react';

interface Props {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  /**
   * Header rendered at the top of the popover body. String values
   * render as an h6; pass any ReactNode (e.g., a status Chip) for
   * non-text headers.
   */
  title?: ReactNode;
  subtitle?: string;
  /**
   * Slot rendered above the title — useful for a small "View on
   * external system" link or a back affordance that the user reaches
   * with minimal travel from the popover trigger.
   */
  headerAction?: ReactNode;
  /** Width preset, defaults to 560px to match the drift-resolution popover. */
  width?: number | string;
  /** Where the popover attaches to its anchor. Defaults to bottom-end. */
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  /**
   * Forwards MUI Popover's imperative-actions ref. Consumers whose
   * content can grow or shrink while the popover is open (e.g., a
   * Collapse) call `actionRef.current?.updatePosition()` after the
   * size change so MUI re-runs its viewport-clamp logic and the
   * popover stays inside the visible viewport.
   */
  actionRef?: Ref<PopoverActions>;
  children: ReactNode;
}

const DEFAULT_ANCHOR_ORIGIN: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 'left',
};
const DEFAULT_TRANSFORM_ORIGIN: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'left',
};

export function TooltipDialog(props: Props) {
  const width = props.width ?? 560;
  return (
    <Popover
      action={props.actionRef}
      open={props.open}
      anchorEl={props.anchorEl}
      onClose={props.onClose}
      anchorOrigin={props.anchorOrigin ?? DEFAULT_ANCHOR_ORIGIN}
      transformOrigin={props.transformOrigin ?? DEFAULT_TRANSFORM_ORIGIN}
      slotProps={{
        paper: {
          sx: { width, maxWidth: '100vw', borderRadius: 2 },
        },
      }}
    >
      <Paper elevation={0} sx={{ padding: 3 }}>
        <Stack spacing={2.5}>
          <Box>
            {props.headerAction !== undefined && (
              <Box mb={1}>{props.headerAction}</Box>
            )}
            {typeof props.title === 'string' ? (
              <Typography variant="h6">{props.title}</Typography>
            ) : (
              props.title
            )}
            {props.subtitle !== undefined && (
              <Typography variant="body2" color="text.secondary">
                {props.subtitle}
              </Typography>
            )}
          </Box>
          {props.children}
        </Stack>
      </Paper>
    </Popover>
  );
}
